import Image from 'next/image';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import Button from '@/components/DesignLibrary/Button';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';

import s from './CreditReportModal.module.scss';

type ConfirmIdentityVerificationProps = {
  mobile: string;
  smsMsg: string;
  handleSmfaVerification: () => void;
  handleSendVerificationLink: () => void;
  setIsResendRequest: (value: boolean) => void;
};

const ConfirmIdentityVerification = ({
  mobile,
  smsMsg,
  handleSmfaVerification,
  handleSendVerificationLink,
  setIsResendRequest,
}: ConfirmIdentityVerificationProps): ReactElement => {
  const [resendButtonDisabled, setResendButtonDisabled] = useState<boolean>(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const lastFourMobileDigits = mobile.slice(-4);

  const handleResendLink = (): void => {
    setIsResendRequest(true);

    handleSendVerificationLink();

    setResendButtonDisabled(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setResendButtonDisabled(false), 30000);
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => setResendButtonDisabled(false), 30000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={s.identityVerification}>
      <div className={s.icon}>
        <Image src='/images/credit-report/fingerprint.png' alt='' width={168} height={168} />
        <div className={s.progress}>
          <ProgressIndicator />
        </div>
      </div>
      <h2 className='heading-sm'>Awaiting verification...</h2>
      <Spacer size={1} />
      <p className={s.identityVerificationContent}>
        Click the verification link that has been sent to your mobile phone number ending in
        <span className={s.identityVerificationMobileNumber}>&nbsp;{lastFourMobileDigits}</span>.
      </p>

      {smsMsg && (
        <div>
          <p className={s.identityVerificationContent}>The following message has been sent to your mobile phone:</p>
          <p className={s.identityVerificationMessage}>{smsMsg}</p>
        </div>
      )}
      <Spacer size={1} />

      <div className={s.identityVerificationButtons}>
        <Button
          variant='secondary'
          size='md'
          fullWidth={true}
          disabled={resendButtonDisabled}
          onClick={handleResendLink}
        >
          Resend Link
        </Button>

        <Button variant='primary' size='md' fullWidth={true} onClick={handleSmfaVerification} className={s.noodleColor}>
          Verification completed, continue
        </Button>
      </div>
    </div>
  );
};

export default ConfirmIdentityVerification;
